import React, { useContext, useEffect } from 'react';
import Cookies from 'js-cookie';
import { useFetch } from 'src/lib/FetchContext';
import { SsrInitDataContext } from 'src/lib/SsrInitDataContext';
import { sendHotjarIdentify } from './hotjarUtils';

interface ILoginResult {
  success?: boolean;
  error?: boolean;
  redirectTo?: string;
}

interface ILoginContext {
  isLoggedIn: boolean;
  canBid: boolean;
  canSell: boolean;
  youHaveBeenLoggedOut: boolean;
  clearYouHaveBeenLoggedOut: () => void;
  login: (email: string, password: string, turnstileToken: any) => Promise<ILoginResult>;
  logout: () => Promise<void>;
  validateAccount: () => Promise<boolean>;
}

export const LoginContext = React.createContext<Partial<ILoginContext>>({});

export const LoginContextConsumer = LoginContext.Consumer;
export const LoginContextProvider = ({ children }) => {
  const { fetch } = useFetch();

  const { ssr, hasLoginToken } = useContext(SsrInitDataContext);
  let initLoginStatus = false;
  if(ssr) {
    initLoginStatus = hasLoginToken;
  } else {
    initLoginStatus = Cookies.get('RIAC_RESTKEY') ? true : false;
  }
  const [isLoggedIn, innerSetIsLoggedIn] = React.useState(initLoginStatus);
  const [canBid, setCanBid] = React.useState(true);
  const [canSell, setCanSell] = React.useState(true);
  const [registrationComplete, setRegistrationComplete] = React.useState(true);

  const [youHaveBeenLoggedOut, setYouHaveBeenLoggedOut] = React.useState(false);
  let validateInterval = -1;
  const setIsLoggedIn = (loggedIn: boolean) => {
    innerSetIsLoggedIn(loggedIn);
    clearInterval(validateInterval);
    if(loggedIn) {
      validateInterval = setInterval(validateAccount, 15000);
    } else {
      validateInterval = -1;
    }
  };

  const clearRestKeyCookie = (): void => {
    const keys = ['RIAC_RESTKEY', 'GA4ID'];
    const domains = ['www.rockislandauction.com', '.rockislandauction.com', 'rockislandauction.com'];
    keys.forEach((key) => {
      domains.forEach((domain) => {
        Cookies.remove(key, { domain });
      });
      Cookies.remove(key);
    });
  };

  const validateAccount = async (first = false) => {
    try {
      const validateResponse = await fetch('/api/account/validate', { credentials: 'include' });
      const validateResult = await validateResponse.json();
      if(!validateResult.Success) {
        clearRestKeyCookie();
        setIsLoggedIn(false);
        setCanBid(false);
        setCanSell(false);
        setRegistrationComplete(false);
        if(!first) {
          setYouHaveBeenLoggedOut(true);
        }
        return false;
      }
      setCanBid(!!validateResult.canBid);
      setCanSell(!!validateResult.canSell);
      setRegistrationComplete(!!validateResult.registrationComplete);
      if(!isLoggedIn) {
        setIsLoggedIn(true);
        setYouHaveBeenLoggedOut(false);
      }
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const login = async (email: string, password: string, turnstileToken: any) => {
    clearRestKeyCookie();
    const loginResponse = await fetch('/api/account/login', {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
        password: password,
        turnstileToken: turnstileToken,
      }),
    });
    const loginResult = await loginResponse.json();
    if(loginResult.Success) {
      Cookies.set('RIAC_RESTKEY', loginResult.RESTKEY, { expires: 365 });
      Cookies.set('GA4ID', loginResult.GA4ID, { expires: 365 });
      if(typeof window.gtag !== 'undefined') {
        window.gtag('config', 'G-2ZR69G6NKP', {
          user_id: loginResult.GA4ID,
        });
      }

      (HOTJAR_SITE_ID) && sendHotjarIdentify({ isLoggedIn: true });

      setIsLoggedIn(true);
      setYouHaveBeenLoggedOut(false);

      // Check registration status to determine redirect
      if(ENABLE_NEW_REGISTRATION) {
        try {
          const validateResp = await fetch('/api/account/validate', { credentials: 'include' });
          const validateData = await validateResp.json();
          if(validateData.Success) {
            setCanBid(!!validateData.canBid);
            setCanSell(!!validateData.canSell);
            setRegistrationComplete(!!validateData.registrationComplete);
            if(!validateData.canBid && !validateData.canSell) {
              return { success: true, redirectTo: '/register' };
            }
          }
        } catch {/* non-fatal — proceed with normal redirect */}
      }

      return { success: true };
    } else {
      return { error: loginResult.Error || 'An unkown error has occured. Please contact customer support' };
    }
  };

  const logout = async () => {
    await fetch('/api/account/logout', {
      credentials: 'include',
      method: 'POST',
    });
    clearRestKeyCookie();
    setCanBid(false);
    setCanSell(false);
    try {localStorage.removeItem('riac_registration');} catch {/* ignore */}
    try {
      Object.keys(sessionStorage).forEach((key) => {
        if(key.startsWith('timed_agreed')) {
          sessionStorage.removeItem(key);
        }
      });
    } catch {/* ignore */}

    (HOTJAR_SITE_ID) && sendHotjarIdentify({ isLoggedIn: false });

    setTimeout(() => {
      if(typeof window.gtag !== 'undefined') {
        window.gtag('config', 'G-2ZR69G6NKP', {
          user_id: null,
        });
      }
      if(location.pathname.includes('/account')) {
        window.location.assign('/login');
      } else location.reload();
    }, 1);
  };

  useEffect(() => {
    if(isLoggedIn) {
      setIsLoggedIn(true);
    }
    validateAccount(true);
  }, []);

  return (
    <LoginContext.Provider value={{
      isLoggedIn,
      canBid,
      canSell,
      registrationComplete,
      youHaveBeenLoggedOut,
      clearYouHaveBeenLoggedOut: () => setYouHaveBeenLoggedOut(false),
      login,
      logout,
      validateAccount,
    }}>
      {children}
    </LoginContext.Provider>
  );
};
